import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';
import { Plus, FileText, ChevronRight, Loader2, Archive } from 'lucide-react';

export function FormTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', category: '' });
  const [saving, setSaving] = useState(false);

  const fetchTemplates = async () => {
    try {
      const res = await formsAPI.listTemplates({ active_only: false });
      setTemplates(res.data?.templates || []);
    } catch { toast.error('Failed to load templates'); }
    setLoading(false);
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) return;
    setSaving(true);
    try {
      const res = await formsAPI.createTemplate({
        name: createForm.name.trim(),
        description: createForm.description.trim() || null,
        category: createForm.category.trim() || null,
      });
      toast.success('Template created');
      setShowCreate(false);
      setCreateForm({ name: '', description: '', category: '' });
      navigate(`/forms/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="form-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Form Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Create reusable forms for job checklists, inspections, and data collection</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="w-12 h-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No form templates yet</p>
            <Button variant="outline" className="mt-4" onClick={() => setShowCreate(true)}>Create your first template</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {templates.map(t => (
            <Card key={t.id} className={`cursor-pointer hover:border-primary/40 transition-colors ${!t.is_active ? 'opacity-60' : ''}`} onClick={() => navigate(`/forms/${t.id}`)} data-testid={`template-card-${t.id}`}>
              <CardContent className="flex items-center gap-4 py-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm truncate">{t.name}</h3>
                    {!t.is_active && <Badge variant="secondary" className="text-xs">Archived</Badge>}
                    {t.is_default && <Badge className="text-xs bg-blue-100 text-blue-700">Auto</Badge>}
                    {t.category && <Badge variant="outline" className="text-xs">{t.category}</Badge>}
                  </div>
                  {t.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{t.description}</p>}
                  <p className="text-xs text-muted-foreground mt-1">{t.fields?.length || 0} fields · {t.submission_count || 0} submissions</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Sheet open={showCreate} onOpenChange={setShowCreate}>
        <SheetContent>
          <SheetHeader><SheetTitle>New Form Template</SheetTitle></SheetHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-6">
            <div>
              <Label>Name *</Label>
              <Input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. HVAC Install Checklist" data-testid="template-name-input" autoFocus />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} placeholder="What is this form for?" rows={2} data-testid="template-description-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={createForm.category} onChange={e => setCreateForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. HVAC, Electrical, General" data-testid="template-category-input" />
            </div>
            <Button type="submit" disabled={saving || !createForm.name.trim()} className="w-full" data-testid="create-template-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Create Template
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
    </Layout>
  );
}

export default FormTemplates;
